import React, { useCallback, useState } from 'react';
import { FileUploadWrapper } from '../../upload/FileUploadWrapper';
import { SalvButton, SalvModal, Size } from '@salvapp/ui-components';
import { faUpload } from '@fortawesome/pro-solid-svg-icons';
import { InternalApiUniversalDocumentEntityType } from '../../api-internal';
import { universalDocumentsApi } from '../../apis';

export interface DocumentRequest {
  entityType: InternalApiUniversalDocumentEntityType;
  entityId?: number;
  entityRef?: string;
}

interface DocumentUploadModalProps {
  forEntity: DocumentRequest;
  refreshDocuments: () => void;
}

export const DocumentUploadModal = ({ forEntity, refreshDocuments }: DocumentUploadModalProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const [files, setFiles] = useState<File[]>([]);

  const upload = useCallback(async () => {
    const remaining: File[] = [];
    let succeeded = 0;
    for (const file of files) {
      try {
        await universalDocumentsApi.uploadDocuments({
          entityType: forEntity.entityType,
          entityId: forEntity.entityId,
          entityRef: forEntity.entityRef,
          file: file,
        });
        succeeded++;
      } catch {
        // The global API error middleware already surfaces the backend message via a toast.
        // Keep the rejected file in the list so the user can see which ones still need attention
        // and continue uploading the rest of the batch.
        remaining.push(file);
      }
    }
    if (succeeded > 0) {
      refreshDocuments();
    }
    setFiles(remaining);
    if (remaining.length === 0) {
      setIsOpen(false);
    }
  }, [files, forEntity, refreshDocuments]);

  return (
    <>
      <SalvButton
        label='Upload file'
        size={Size.SMALL}
        variant='secondary'
        icon={faUpload}
        onClick={() => setIsOpen(true)}
      />
      <SalvModal show={isOpen} onHide={() => setIsOpen(false)} allowDismissal={true}>
        <SalvModal.Header closeButton>Upload Documents</SalvModal.Header>
        <SalvModal.Body>
          <FileUploadWrapper files={files} onChange={setFiles} isMulti={true} />
        </SalvModal.Body>
        <SalvModal.Footer>
          <SalvButton size={Size.MEDIUM} label='Cancel' variant='muted' onClick={() => setIsOpen(false)} />
          <SalvButton size={Size.MEDIUM} label='Upload' variant='primary' onClick={upload} />
        </SalvModal.Footer>
      </SalvModal>
    </>
  );
};
